import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Copy, Inbox, Loader2, Search, X, AlertTriangle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

// Bộ component dùng chung, thiết kế mobile-first:
// - Mặc định cho màn hình nhỏ (vùng chạm ~44px, nội dung dạng thẻ), desktop gọn hơn và dùng bảng
// - Nút chính màu mực đậm; màu đỏ chỉ dùng cho nguy hiểm/từ chối để không nhầm với hành động duyệt

export const cx = (...classes) => classes.filter(Boolean).join(' ');

export const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const BUTTON_VARIANTS = {
  primary: 'bg-gray-900 text-white hover:bg-gray-800 active:bg-gray-950',
  success: 'bg-emerald-700 text-white hover:bg-emerald-800 active:bg-emerald-900',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
  warning: 'bg-amber-500 text-white hover:bg-amber-600',
  outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 active:bg-gray-100',
  ghost: 'text-gray-600 hover:bg-gray-100 active:bg-gray-200',
  soft: 'bg-gray-200/70 text-gray-900 hover:bg-gray-200'
};

const BUTTON_SIZES = {
  sm: 'h-9 px-3 text-sm gap-1.5 md:h-8',
  md: 'h-11 px-4 text-sm gap-2 md:h-9',
  icon: 'h-10 w-10 md:h-9 md:w-9'
};

export const Button = ({ variant = 'primary', size = 'md', loading = false, className, children, disabled, ...props }) => (
  <button
    type="button"
    className={cx(
      'inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
      BUTTON_VARIANTS[variant],
      BUTTON_SIZES[size],
      className
    )}
    disabled={disabled || loading}
    {...props}
  >
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
    {children}
  </button>
);

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="mb-4 flex flex-col gap-3 md:mb-5 md:flex-row md:items-end md:justify-between">
    <div className="min-w-0">
      <h1 className="truncate text-xl font-semibold tracking-tight text-gray-900 md:text-2xl">{title}</h1>
      {subtitle && <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const Card = ({ className, children, as: Tag = 'div', ...props }) => (
  <Tag className={cx('min-w-0 rounded-xl border border-gray-200 bg-white', className)} {...props}>
    {children}
  </Tag>
);

export const SectionTitle = ({ children, right, className }) => (
  <div className={cx('mb-2 flex items-center justify-between gap-2', className)}>
    <h2 className="text-sm font-semibold text-gray-900">{children}</h2>
    {right}
  </div>
);

const BADGE_TONES = {
  gray: 'bg-gray-100 text-gray-700 ring-gray-500/15',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  yellow: 'bg-amber-50 text-amber-800 ring-amber-600/25',
  red: 'bg-red-50 text-red-700 ring-red-600/20',
  blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  indigo: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  purple: 'bg-purple-50 text-purple-700 ring-purple-600/20',
  orange: 'bg-orange-50 text-orange-700 ring-orange-600/20'
};

export const Badge = ({ tone = 'gray', className, children }) => (
  <span className={cx('inline-flex items-center whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset', BADGE_TONES[tone], className)}>
    {children}
  </span>
);

const ORDER_STATUS = {
  COMPLETED: { tone: 'green', label: 'Hoàn thành' },
  PENDING: { tone: 'yellow', label: 'Chờ xử lý' },
  CANCELLED: { tone: 'red', label: 'Đã hủy' },
  UNPAID: { tone: 'gray', label: 'Chưa thanh toán' },
  PROCESSING: { tone: 'blue', label: 'Đang xử lý' }
};

export const OrderStatusBadge = ({ status }) => {
  const key = String(status || '').toUpperCase();
  const cfg = ORDER_STATUS[key] || { tone: 'gray', label: status || 'UNKNOWN' };
  return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
};

// Sàn của đơn (orders.platform); đơn cũ không có field này là Shopee
const ORDER_PLATFORM = {
  shopee: { tone: 'orange', label: 'Shopee' },
  tiktokshop: { tone: 'gray', label: 'TikTok' },
  lazada: { tone: 'indigo', label: 'Lazada' }
};
export const ORDER_PLATFORM_TABS = [
  { value: '', label: 'Mọi sàn' },
  { value: 'shopee', label: 'Shopee' },
  { value: 'tiktokshop', label: 'TikTok Shop' },
  { value: 'lazada', label: 'Lazada' }
];

export const PlatformBadge = ({ platform }) => {
  const cfg = ORDER_PLATFORM[platform || 'shopee'] || { tone: 'gray', label: platform };
  return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
};

const WITHDRAWAL_STATUS = {
  PENDING: { tone: 'yellow', label: 'Chờ duyệt' },
  COMPLETED: { tone: 'green', label: 'Đã chuyển' },
  CANCELLED: { tone: 'red', label: 'Từ chối' }
};

export const WithdrawalStatusBadge = ({ status }) => {
  const cfg = WITHDRAWAL_STATUS[status] || { tone: 'gray', label: status };
  return <Badge tone={cfg.tone}>{cfg.label}</Badge>;
};

export const Spinner = ({ className }) => <Loader2 className={cx('h-5 w-5 animate-spin text-gray-500', className)} />;

export const LoadingBlock = ({ label = 'Đang tải...' }) => (
  <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
    <Spinner /> {label}
  </div>
);

// Khung xương khi tải danh sách thẻ
export const SkeletonList = ({ rows = 4 }) => (
  <div className="space-y-2">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-200/60" />
    ))}
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title = 'Không có dữ liệu', description, action }) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 px-4 py-12 text-center">
    <div className="mb-3 rounded-lg bg-gray-100 p-2.5">
      <Icon className="h-5 w-5 text-gray-500" />
    </div>
    <p className="text-sm font-medium text-gray-900">{title}</p>
    {description && <p className="mt-1 max-w-sm text-sm text-gray-500">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const ErrorBanner = ({ message, onRetry }) => {
  if (!message) return null;
  return (
    <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="flex-1">{message}</div>
      {onRetry && (
        <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline">
          <RefreshCw className="h-3.5 w-3.5" /> Thử lại
        </button>
      )}
    </div>
  );
};

const FIELD_BASE = 'w-full rounded-lg border border-gray-300 bg-white text-base outline-none transition placeholder:text-gray-400 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 md:text-sm';

export const SearchInput = ({ value, onChange, placeholder = 'Tìm kiếm...', className, ...props }) => (
  <div className={cx('relative', className)}>
    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cx(FIELD_BASE, 'h-11 pl-9 pr-9 md:h-9')}
      {...props}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-gray-100"
        aria-label="Xóa"
      >
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

export const inputClass = cx(FIELD_BASE, 'h-11 px-3 md:h-9');

// Ô nhiều dòng: chiều cao đặt qua rows hoặc class của nơi dùng
export const textareaClass = cx(FIELD_BASE, 'px-3 py-2');

export const Field = ({ label, hint, children, className }) => (
  <label className={cx('block', className)}>
    {label && <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>}
    {children}
    {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
  </label>
);

// Thanh chọn liền khối, cuộn ngang được trên mobile
export const Segmented = ({ options, value, onChange, className }) => (
  <div className={cx('-mx-4 min-w-0 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0', className)}>
    <div className="inline-flex gap-0.5 rounded-lg bg-gray-200/70 p-0.5">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={cx(
              'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 md:h-8',
              active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            )}
          >
            {opt.label}
            {opt.count !== undefined && opt.count !== null && (
              <span className={cx('tabular rounded px-1 text-xs', active ? 'bg-gray-100 text-gray-700' : 'bg-gray-300/50 text-gray-600')}>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

export const Pagination = ({ page, totalPages, total, onChange, limit, onLimitChange, disabled }) => {
  const pages = Math.max(1, totalPages || 1);
  return (
    <div className="flex items-center justify-between gap-3 py-3 text-sm text-gray-600">
      <div className="hidden sm:block">
        Tổng <span className="tabular font-medium text-gray-900">{total ?? 0}</span>
      </div>
      <div className="flex flex-1 items-center justify-between gap-2 sm:flex-none sm:justify-end">
        {onLimitChange && (
          <select
            className="h-10 rounded-lg border border-gray-300 bg-white px-2 text-sm md:h-9"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            disabled={disabled}
            aria-label="Số dòng mỗi trang"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>{n}/trang</option>
            ))}
          </select>
        )}
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" onClick={() => onChange(page - 1)} disabled={disabled || page <= 1} aria-label="Trang trước">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="tabular min-w-[4.5rem] text-center font-medium text-gray-900">
            {page} / {pages}
          </span>
          <Button variant="outline" size="icon" onClick={() => onChange(page + 1)} disabled={disabled || page >= pages} aria-label="Trang sau">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Bottom sheet trên mobile, modal giữa màn hình trên desktop
export const Sheet = ({ open, onClose, title, children, footer, size = 'md' }) => {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  const width = { sm: 'md:max-w-md', md: 'md:max-w-lg', lg: 'md:max-w-3xl', xl: 'md:max-w-5xl' }[size];

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center md:items-center md:p-4">
      <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={cx(
          'relative flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-white shadow-xl md:rounded-xl',
          width
        )}
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-gray-300 md:hidden" />
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <h3 className="truncate text-base font-semibold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100" aria-label="Đóng">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">{children}</div>
        {footer && (
          <div className="border-t border-gray-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:pb-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const CopyButton = ({ value, label = 'Sao chép' }) => {
  if (!value) return null;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success('Đã sao chép');
    } catch (_) {
      toast.error('Không sao chép được');
    }
  };
  return (
    <button type="button" onClick={copy} className="inline-flex items-center rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label={label} title={label}>
      <Copy className="h-4 w-4" />
    </button>
  );
};

// Hàng "nhãn — giá trị" trong thẻ chi tiết
export const InfoRow = ({ label, children, mono }) => (
  <div className="flex items-start justify-between gap-3 py-2 text-sm">
    <span className="flex-shrink-0 text-gray-500">{label}</span>
    <span className={cx('min-w-0 break-words text-right font-medium text-gray-900', mono && 'font-mono text-xs')}>{children}</span>
  </div>
);

export const Avatar = ({ name, email, className }) => {
  const ch = (name || email || 'U').trim().charAt(0).toUpperCase();
  return (
    <div className={cx('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-semibold text-gray-700', className)}>
      {ch}
    </div>
  );
};

// Hiển thị bảng trên desktop, danh sách thẻ trên mobile, dùng chung 1 nguồn dữ liệu
export const ResponsiveList = ({ items, keyField = 'id', renderCard, columns, onRowClick, empty }) => {
  if (!items || items.length === 0) return empty || <EmptyState />;
  return (
    <>
      <div className="space-y-2 md:hidden">
        {items.map((item) => (
          <React.Fragment key={item[keyField]}>{renderCard(item)}</React.Fragment>
        ))}
      </div>
      <Card className="hidden overflow-hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cx('whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium text-gray-500', col.align === 'right' && 'text-right', col.thClass)}
                  >
                    {col.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item[keyField]}
                  className={cx('hover:bg-gray-50', onRowClick && 'cursor-pointer')}
                  onClick={onRowClick ? () => onRowClick(item) : undefined}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={cx('px-4 py-2.5', col.align === 'right' && 'whitespace-nowrap text-right', col.tdClass)}>
                      {col.render(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
};
